'use client';

import posthog from 'posthog-js';
import { SignInButton, UserButton, useUser } from '@clerk/nextjs';
import { useCurrency } from './CurrencyContext';
import { useSortBy } from './SortContext';
import { useSubscription } from './SubscriptionContext';
import { useTheme } from './ThemeContext';
import { useChartType } from './ChartTypeContext';
import { Button } from './Button';
import { Crown, CreditCard, ArrowUpDown, Palette, Check, Monitor, FlaskConical, TerminalSquare, Download, Loader2, GraduationCap, LineChart, CandlestickChart, Mail } from 'lucide-react';
import type { Theme } from './ThemeContext';
import type { ChartType } from './ChartTypeContext';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { cn } from '@/lib/cn';
import lightIcon from '@/app/icon.png';
import darkIcon from '@/app/dark-icon.png';
import { getApiBase } from '@/lib/apiBase';
import { useIsMacDesktop } from '@/lib/useIsMacDesktop';
import { exportPortfolioToCSV } from '@/lib/csvExport';

type SortByValue = 'alphabetical' | 'alphabeticalByName' | 'lastUpdated';

const SORT_OPTIONS: { value: SortByValue; label: string; desc: string; icon: string }[] = [
  { value: 'alphabetical', label: 'By Ticker', desc: 'Sort A → Z by ticker symbol', icon: 'AZ' },
  { value: 'alphabeticalByName', label: 'By Name', desc: 'Sort A → Z by stock name', icon: 'Aa' },
  { value: 'lastUpdated', label: 'Last Updated', desc: 'Most recently added or modified first', icon: '🕐' },
];

const THEME_OPTIONS: { value: Theme; label: string; desc: string; gradient: string }[] = [
  { value: 'classic', label: 'Classic', desc: 'Slate & blue tones', gradient: 'linear-gradient(135deg, #0f172a 50%, #2563eb 50%)' },
  { value: 'modern', label: 'Modern', desc: 'Zinc & teal tones', gradient: 'linear-gradient(135deg, #09090b 50%, #0d9488 50%)' },
  { value: 'light', label: 'Light', desc: 'Clean & bright', gradient: 'linear-gradient(135deg, #f8fafc 50%, #2563eb 50%)' },
];

const CHART_TYPE_OPTIONS: { value: ChartType; label: string; desc: string; Icon: typeof LineChart }[] = [
  { value: 'line', label: 'Line', desc: 'Smooth area chart of close prices', Icon: LineChart },
  { value: 'candle', label: 'Candlestick', desc: 'OHLC candles for daily price action', Icon: CandlestickChart },
];

function SortPage({ sortBy, onSelect }: { sortBy: SortByValue; onSelect: (v: SortByValue) => void }) {
  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Sort stocks by</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              border: sortBy === opt.value ? '2px solid var(--color-accent)' : '2px solid transparent',
              backgroundColor: sortBy === opt.value ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)' : 'transparent',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '0.5rem',
              backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              flexShrink: 0,
            }}>
              {opt.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{opt.label}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>{opt.desc}</div>
            </div>
            {sortBy === opt.value && <Check size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function ThemePage({ theme, onSelect }: { theme: Theme; onSelect: (v: Theme) => void }) {
  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Choose a theme</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {THEME_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              border: theme === opt.value ? '2px solid var(--color-accent)' : '2px solid transparent',
              backgroundColor: theme === opt.value ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)' : 'transparent',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              borderRadius: '0.5rem',
              background: opt.gradient,
              flexShrink: 0,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{opt.label}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>{opt.desc}</div>
            </div>
            {theme === opt.value && <Check size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChartTypePage({ chartType, onSelect }: { chartType: ChartType; onSelect: (v: ChartType) => void }) {
  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Default chart type</h2>
      <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '0.75rem' }}>
        Used as the initial chart mode on stock detail pages in both normal and terminal modes.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {CHART_TYPE_OPTIONS.map((opt) => {
          const Icon = opt.Icon;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: chartType === opt.value ? '2px solid var(--color-accent)' : '2px solid transparent',
                backgroundColor: chartType === opt.value ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)' : 'transparent',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <div style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '0.5rem',
                backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'var(--color-accent)',
              }}>
                <Icon size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{opt.label}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>{opt.desc}</div>
              </div>
              {chartType === opt.value && <Check size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResearchPage({ used, limit, resetsAt }: { used: number; limit: number; resetsAt: string | null }) {
  const remaining = limit - used;
  const percentage = Math.min(100, Math.round((used / limit) * 100));
  const resetDate = resetsAt ? new Date(resetsAt) : null;

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Research Reports</h2>

      <div style={{
        padding: '1rem',
        borderRadius: '0.75rem',
        backgroundColor: 'color-mix(in srgb, var(--color-accent) 8%, transparent)',
        border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '2rem', fontWeight: 700 }}>{remaining}</span>
          <span style={{ fontSize: '0.75rem', opacity: 0.6 }}>of {limit} remaining</span>
        </div>

        <div style={{
          width: '100%',
          height: '6px',
          borderRadius: '3px',
          backgroundColor: 'color-mix(in srgb, var(--color-text) 15%, transparent)',
          marginBottom: '0.5rem',
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            borderRadius: '3px',
            backgroundColor: percentage >= 90 ? '#f59e0b' : 'var(--color-accent)',
            transition: 'width 0.3s ease',
          }} />
        </div>

        <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
          {used} of {limit} reports used this month
        </div>
      </div>

      {resetDate && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.75rem',
          opacity: 0.6,
        }}>
          <span>Resets on {resetDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
        </div>
      )}
    </div>
  );
}

function ExportPage() {
  const [isExporting, setIsExporting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleExport = async () => {
    setIsExporting(true);
    setStatus('idle');
    try {
      const res = await fetch(`${getApiBase()}/api/portfolio`);
      if (!res.ok) throw new Error('Failed to fetch portfolio');
      const data = await res.json();
      exportPortfolioToCSV(data.stocks);
      setStatus('success');
    } catch {
      setStatus('error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Export Portfolio</h2>
      <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '1rem' }}>
        Download your portfolio as a CSV file with per-lot detail including current values and gain/loss.
      </p>
      <button
        onClick={handleExport}
        disabled={isExporting}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          width: '100%',
          padding: '0.75rem',
          borderRadius: '0.5rem',
          border: 'none',
          backgroundColor: 'var(--color-accent)',
          color: '#fff',
          fontSize: '0.875rem',
          fontWeight: 500,
          cursor: isExporting ? 'not-allowed' : 'pointer',
          opacity: isExporting ? 0.7 : 1,
        }}
      >
        {isExporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        {isExporting ? 'Exporting...' : 'Download CSV'}
      </button>
      {status === 'success' && (
        <p style={{ fontSize: '0.75rem', color: 'var(--color-positive)', marginTop: '0.5rem' }}>
          Portfolio exported successfully.
        </p>
      )}
      {status === 'error' && (
        <p style={{ fontSize: '0.75rem', color: 'var(--color-negative)', marginTop: '0.5rem' }}>
          Failed to export. Please try again.
        </p>
      )}
    </div>
  );
}

function EmailPreferencesPage() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBase()}/api/preferences`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setEnabled(data.preferences?.marketingEmails !== false);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async () => {
    if (enabled === null || pending) return;
    const next = !enabled;
    setEnabled(next); // optimistic
    setPending(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBase()}/api/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketingEmails: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      posthog.capture('marketing_emails_toggled', { enabled: next });
    } catch {
      setEnabled(!next); // revert
      setError("Couldn't save. Please try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Email preferences</h2>
      <p style={{ fontSize: '0.75rem', opacity: 0.6, marginBottom: '1rem' }}>
        We&apos;ll always send essential account emails (receipts, security notices). This setting only controls product update emails.
      </p>

      <button
        onClick={handleToggle}
        disabled={enabled === null || pending}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.875rem 1rem',
          borderRadius: '0.5rem',
          border: '1px solid color-mix(in srgb, var(--color-text) 12%, transparent)',
          backgroundColor: 'transparent',
          cursor: enabled === null || pending ? 'not-allowed' : 'pointer',
          width: '100%',
          textAlign: 'left',
          opacity: enabled === null ? 0.6 : 1,
        }}
      >
        <div
          style={{
            width: '2.5rem',
            height: '2.5rem',
            borderRadius: '0.5rem',
            backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-accent)',
            flexShrink: 0,
          }}
        >
          <Mail size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>Product update emails</div>
          <div style={{ fontSize: '0.75rem', opacity: 0.6 }}>
            Monthly highlights of new features and improvements.
          </div>
        </div>
        <div
          aria-hidden
          style={{
            position: 'relative',
            width: '2.25rem',
            height: '1.25rem',
            borderRadius: '999px',
            backgroundColor: enabled
              ? 'var(--color-accent)'
              : 'color-mix(in srgb, var(--color-text) 25%, transparent)',
            transition: 'background-color 0.15s ease',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '2px',
              left: enabled ? 'calc(100% - 1rem - 2px)' : '2px',
              width: '1rem',
              height: '1rem',
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.15s ease',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }}
          />
        </div>
      </button>

      {error && (
        <p style={{ fontSize: '0.75rem', color: 'var(--color-negative)', marginTop: '0.5rem' }}>
          {error}
        </p>
      )}
    </div>
  );
}

export function AppHeader() {
  const { isSignedIn } = useUser();
  const { currency, setCurrency } = useCurrency();
  const { sortBy, setSortBy } = useSortBy();
  const { tier, subscriptionMethod, isLoading } = useSubscription();
  const { theme, setTheme } = useTheme();
  const { chartType, setChartType } = useChartType();
  const router = useRouter();
  const isMacDesktop = useIsMacDesktop();
  const [researchUsage, setResearchUsage] = useState({ used: 0, limit: 50, resetsAt: null as string | null });
  const [portfolioIsEmpty, setPortfolioIsEmpty] = useState<boolean | null>(null);

  useEffect(() => {
    if (isSignedIn && tier === 'paid') {
      fetch(`${getApiBase()}/api/research/usage`)
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setResearchUsage({ used: data.used, limit: data.limit, resetsAt: data.resetsAt }); })
        .catch(() => {});
    }
  }, [isSignedIn, tier]);

  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    fetch(`${getApiBase()}/api/portfolio`)
      .then((res) => {
        if (res.status === 404) return { stocks: {} };
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        if (cancelled || !data) return;
        const count = data.stocks ? Object.keys(data.stocks).length : 0;
        setPortfolioIsEmpty(count === 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isSignedIn]);

  const showLearnLink = !isSignedIn || portfolioIsEmpty === true;

  const handleManageSubscription = async () => {
    posthog.capture('manage_subscription_clicked', {
      tier,
    });

    try {
      const response = await fetch(`${getApiBase()}/api/stripe/create-portal`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Failed to open portal:', error);
      posthog.captureException(error);
    }
  };

  const handleSortSelect = (newSort: SortByValue) => {
    if (newSort !== sortBy) {
      posthog.capture('sort_toggled', { from: sortBy, to: newSort });
      setSortBy(newSort);
    }
  };

  const handleThemeSelect = (newTheme: Theme) => {
    if (newTheme !== theme) {
      posthog.capture('theme_toggled', { from: theme, to: newTheme });
      setTheme(newTheme);
    }
  };

  const handleChartTypeSelect = (newChartType: ChartType) => {
    if (newChartType !== chartType) {
      posthog.capture('chart_type_toggled', { from: chartType, to: newChartType });
      setChartType(newChartType);
    }
  };

  const handleCurrencyChange = (newCurrency: 'USD' | 'CAD') => {
    if (currency !== newCurrency) {
      posthog.capture('currency_toggled', {
        from: currency,
        to: newCurrency,
      });
      setCurrency(newCurrency);
    }
  };

  return (
    <header className={cn("relative backdrop-blur-md sticky top-0 z-50", "after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gradient-to-r after:from-transparent after:via-[var(--color-border)] after:to-transparent")} style={{ backgroundColor: 'var(--color-bg)' }}>
      <div className="container mx-auto px-4 py-4 flex items-center justify-between min-h-[3.5rem]">
        <Link href="/" className="flex items-center gap-2">
          <Image
            src={theme === 'light' ? lightIcon : darkIcon}
            alt="EquityLabs"
            width={28}
            height={28}
            className="rounded-md"
            priority
          />
          <h1 className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>Equity<span style={{ color: 'var(--color-accent-muted)' }}>Labs</span></h1>
        </Link>

        <div className="flex items-center gap-2 sm:gap-4">
          {/* New to investing — visible for signed-out users or signed-in users with empty portfolio */}
          {showLearnLink && (
            <Link
              href="/learn"
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
              style={{
                color: 'var(--color-accent-muted)',
                backgroundColor: 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-accent) 18%, transparent)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-accent) 10%, transparent)'}
            >
              <GraduationCap size={14} />
              <span className="hidden sm:inline">New to investing</span>
              <span className="sm:hidden">Learn</span>
            </Link>
          )}

          {/* Terminal Link */}
          {isSignedIn && (
            <Link
              href="/terminal"
              aria-label="Terminal"
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-xs font-medium rounded-lg transition-colors hover:bg-[var(--color-surface)]"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <TerminalSquare size={14} />
              <span className="hidden sm:inline">Terminal</span>
            </Link>
          )}

          {/* Currency Toggle */}
          <div className="flex items-center rounded-full p-1" style={{ backgroundColor: 'var(--color-toggle-bg)' }}>
            <button
              onClick={() => handleCurrencyChange('USD')}
              className={cn(
                "rounded-full px-2 sm:px-3 py-1 text-xs font-medium transition-colors",
                currency === 'USD' ? 'shadow' : 'hover:text-[var(--color-text-primary)]'
              )}
              style={currency === 'USD' ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
            >
              USD
            </button>
            <button
              onClick={() => handleCurrencyChange('CAD')}
              className={cn(
                "rounded-full px-2 sm:px-3 py-1 text-xs font-medium transition-colors",
                currency === 'CAD' ? 'shadow' : 'hover:text-[var(--color-text-primary)]'
              )}
              style={currency === 'CAD' ? { backgroundColor: 'var(--color-toggle-active-accent)', color: '#fff' } : { color: 'var(--color-text-secondary)' }}
            >
              CAD
            </button>
          </div>

          {/* Subscription Status + Auth UI */}
          {isSignedIn ? (
            <div className="flex items-center gap-2 sm:gap-3">
              {!isLoading && tier === 'free' && (
                <Button
                  size="sm"
                  onClick={() => {
                    posthog.capture('upgrade_button_clicked', {
                      trigger: 'manual_upgrade_button',
                    });
                    router.push('/checkout');
                  }}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 border-0 !px-2 sm:!px-3"
                >
                  <Crown size={14} className="sm:mr-1" />
                  <span className="hidden sm:inline">Upgrade</span>
                </Button>
              )}
              <span className="hidden sm:inline text-sm" style={{ color: 'var(--color-text-secondary)' }}>Synced</span>
              {/* Paid users: Crown + Avatar wrapped together */}
              {!isLoading && tier === 'paid' ? (
                <div className="flex items-center gap-1.5 sm:gap-2 pl-2 sm:pl-3 pr-1 py-1 rounded-full bg-gradient-to-r from-yellow-500/15 to-orange-500/15 border border-yellow-500/40">
                  <Crown size={14} className="text-yellow-500" />
                  <span className="hidden sm:inline text-sm font-medium text-yellow-500">Premium</span>
                  <UserButton
                    appearance={{
                      elements: {
                        avatarBox: 'w-8 h-8 sm:w-9 sm:h-9',
                      },
                    }}
                  >
                    <UserButton.MenuItems>
                      <UserButton.Action
                        label="Sort"
                        labelIcon={<ArrowUpDown size={16} />}
                        open="sort"
                      />
                      <UserButton.Action
                        label="Theme"
                        labelIcon={<Palette size={16} />}
                        open="theme"
                      />
                      <UserButton.Action
                        label="Chart Type"
                        labelIcon={<LineChart size={16} />}
                        open="chart-type"
                      />
                      <UserButton.Action
                        label="Research"
                        labelIcon={<FlaskConical size={16} />}
                        open="research"
                      />
                      <UserButton.Action
                        label="Export"
                        labelIcon={<Download size={16} />}
                        open="export"
                      />
                      <UserButton.Action
                        label="Email preferences"
                        labelIcon={<Mail size={16} />}
                        open="email-preferences"
                      />
                      {subscriptionMethod !== 'app_store_subscription' && (
                        <UserButton.Action
                          label="Manage Subscription"
                          labelIcon={<CreditCard size={16} />}
                          onClick={handleManageSubscription}
                        />
                      )}
                      {isMacDesktop && (
                        <UserButton.Action
                          label="Download for Mac"
                          labelIcon={<Monitor size={16} />}
                          onClick={() => router.push('/download')}
                        />
                      )}
                    </UserButton.MenuItems>
                    <UserButton.UserProfilePage
                      label="Sort"
                      labelIcon={<ArrowUpDown size={16} />}
                      url="sort"
                    >
                      <SortPage sortBy={sortBy} onSelect={handleSortSelect} />
                    </UserButton.UserProfilePage>
                    <UserButton.UserProfilePage
                      label="Theme"
                      labelIcon={<Palette size={16} />}
                      url="theme"
                    >
                      <ThemePage theme={theme} onSelect={handleThemeSelect} />
                    </UserButton.UserProfilePage>
                    <UserButton.UserProfilePage
                      label="Chart Type"
                      labelIcon={<LineChart size={16} />}
                      url="chart-type"
                    >
                      <ChartTypePage chartType={chartType} onSelect={handleChartTypeSelect} />
                    </UserButton.UserProfilePage>
                    <UserButton.UserProfilePage
                      label="Research"
                      labelIcon={<FlaskConical size={16} />}
                      url="research"
                    >
                      <ResearchPage
                        used={researchUsage.used}
                        limit={researchUsage.limit}
                        resetsAt={researchUsage.resetsAt}
                      />
                    </UserButton.UserProfilePage>
                    <UserButton.UserProfilePage
                      label="Export"
                      labelIcon={<Download size={16} />}
                      url="export"
                    >
                      <ExportPage />
                    </UserButton.UserProfilePage>
                    <UserButton.UserProfilePage
                      label="Email preferences"
                      labelIcon={<Mail size={16} />}
                      url="email-preferences"
                    >
                      <EmailPreferencesPage />
                    </UserButton.UserProfilePage>
                  </UserButton>
                </div>
              ) : (
                <UserButton
                  appearance={{
                    elements: {
                      avatarBox: 'w-10 h-10',
                    },
                  }}
                >
                  <UserButton.MenuItems>
                    <UserButton.Action
                      label="Sort"
                      labelIcon={<ArrowUpDown size={16} />}
                      open="sort"
                    />
                    <UserButton.Action
                      label="Theme"
                      labelIcon={<Palette size={16} />}
                      open="theme"
                    />
                    <UserButton.Action
                      label="Chart Type · Paid feature"
                      labelIcon={<span style={{ opacity: 0.4 }}><LineChart size={16} /></span>}
                      onClick={() => router.push('/checkout')}
                    />
                    <UserButton.Action
                      label="Research · Paid feature"
                      labelIcon={<span style={{ opacity: 0.4 }}><FlaskConical size={16} /></span>}
                      onClick={() => router.push('/checkout')}
                    />
                    <UserButton.Action
                      label="Export · Paid feature"
                      labelIcon={<span style={{ opacity: 0.4 }}><Download size={16} /></span>}
                      onClick={() => router.push('/checkout')}
                    />
                    <UserButton.Action
                      label="Email preferences"
                      labelIcon={<Mail size={16} />}
                      open="email-preferences"
                    />
                    {isMacDesktop && (
                      <UserButton.Action
                        label="Download for Mac"
                        labelIcon={<Monitor size={16} />}
                        onClick={() => router.push('/download')}
                      />
                    )}
                  </UserButton.MenuItems>
                  <UserButton.UserProfilePage
                    label="Sort"
                    labelIcon={<ArrowUpDown size={16} />}
                    url="sort"
                  >
                    <SortPage sortBy={sortBy} onSelect={handleSortSelect} />
                  </UserButton.UserProfilePage>
                  <UserButton.UserProfilePage
                    label="Theme"
                    labelIcon={<Palette size={16} />}
                    url="theme"
                  >
                    <ThemePage theme={theme} onSelect={handleThemeSelect} />
                  </UserButton.UserProfilePage>
                  <UserButton.UserProfilePage
                    label="Email preferences"
                    labelIcon={<Mail size={16} />}
                    url="email-preferences"
                  >
                    <EmailPreferencesPage />
                  </UserButton.UserProfilePage>
                </UserButton>
              )}
            </div>
          ) : (
            <SignInButton mode="modal">
              <button className="px-4 py-2 text-white rounded-xl text-sm font-medium transition-colors" style={{ backgroundColor: 'var(--color-accent)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--color-accent)'}>
                Sign In
              </button>
            </SignInButton>
          )}
        </div>
      </div>
    </header>
  );
}
